/* SPEC-506 — calls to /api/feedback/* and the operator's intake. Kept out of lib/api.ts on
 * purpose (that file is CRLF and edited by many specs in parallel). JSON calls go through the
 * shared `request` (token refresh, error unwrapping); the screenshot is read as a blob. */
import { request } from '@/lib/api';
import type { FeedbackCategory } from './core';

export interface FeedbackConfig {
  auto_capture: boolean;
  /** The app's /api route templates; a recorded call is mapped to one or dropped. */
  routes: string[];
  /** The app's known query parameters and their types; nothing else is sent. */
  filter_params: Record<string, string>;
  /** The app's page routes; a page is stored as one of their templates. */
  pages: string[];
  max_bytes: number;
  max_px: number;
  categories: Array<{ key: FeedbackCategory; label: string }>;
}

export interface SubmitResult {
  id: string;
  status: string;
  routed_to: string;
  screenshot_saved: boolean;
  storage_note: string | null;
  message: string;
}

/** A report's category as stored. `feature_request` exists only on asks migrated from the
 *  retired request form (SPEC-521); nothing new is filed under it. */
export type StoredCategory = FeedbackCategory | 'feature_request';

export interface MyReport {
  id: string;
  category: StoredCategory;
  category_label: string;
  text: string;
  expected: string | null;
  blocking: boolean | null;
  filed_from: string | null;
  submitted_at: string | null;
  status: string;
  status_label: string;
  status_changed_at: string | null;
  reply: string | null;
  /** `released_on` is the release note's date (YYYY-MM-DD); `live_since` is set only when shipping
   *  is proved (a spec merge reachable from main) — SPEC-521. */
  fixed_in: { spec: string; title: string | null; link: string | null; released_on?: string | null; live_since?: string | null } | null;
  has_screenshot: boolean;
}

export interface OwnerResponse { response: 'agree' | 'not_now' | 'comment'; comment: string | null; by: string; at: string }

export interface RoutedReport {
  id: string;
  category: FeedbackCategory;
  category_label: string;
  text: string;
  filed_from: string | null;
  submitted_by: string;
  submitted_at: string | null;
  owner_response: OwnerResponse | null;
}

export interface IntakeFeedbackItem {
  id: string;
  source: string;
  author: string;
  text: string;
  category?: StoredCategory;
  category_label?: string;
  report_text?: string;
  expected?: string | null;
  blocking?: boolean | null;
  filed_from?: string | null;
  page?: string | null;
  url?: string | null;
  context?: Record<string, unknown> | null;
  has_screenshot?: boolean;
  routed_to?: string | null;
  owner_response?: OwnerResponse | null;
  reply?: string | null;
  changelog_spec?: string | null;
  /** SPEC-521: "scenario:<id>" / "feature:<id>" when migrated from a retired store. */
  legacy_ref?: string | null;
  /** SPEC-521 fix loop 1: the operator's triage note — admin view only, never shown to the reporter. */
  internal_note?: string | null;
  live_since?: string | null;
  report_status?: string;
  report_status_label?: string;
  duplicate_hints?: string[];
  acked?: boolean;
  created_at: string | null;
}

export interface ReportsDigest {
  open: number;
  open_by_age: { under_7_days: number; '7_to_30_days': number; over_30_days: number };
  waiting_on_reporter: number;
  blocking_open: number;
}

export const fetchFeedbackConfig = () => request<FeedbackConfig>('/api/feedback/config');
export const fetchMyReports = () => request<{ items: MyReport[] }>('/api/feedback/mine');
export const fetchRoutedReports = () => request<{ items: RoutedReport[] }>('/api/feedback/routed');

export function submitReport(payload: object, screenshot: Blob | null): Promise<SubmitResult> {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));
  if (screenshot) {
    const ext = screenshot.type === 'image/png' ? 'png' : screenshot.type === 'image/webp' ? 'webp' : 'jpg';
    form.append('screenshot', screenshot, `screenshot.${ext}`);
  }
  return request<SubmitResult>('/api/feedback', { method: 'POST', body: form });
}

export const respondAsOwner = (id: string, response: OwnerResponse['response'], comment?: string) =>
  request<{ id: string; owner_response: OwnerResponse }>(`/api/feedback/${encodeURIComponent(id)}/owner-response`, {
    method: 'POST', body: JSON.stringify({ response, comment: comment ?? null }),
  });

export const fetchIntakeForTriage = () =>
  request<{ items: IntakeFeedbackItem[]; count: number; feedback_digest?: { reports?: ReportsDigest | null } }>(
    '/api/admin/intake?include_open=true');

export const triageReport = (id: string, body: { status?: string; reply?: string; changelog_spec?: string; disposition?: string; evidence?: string }) =>
  request<any>(`/api/admin/intake/${encodeURIComponent(id)}/ack`, {
    method: 'POST', body: JSON.stringify({ source: 'feedback', ...body }),
  });

/** The screenshot needs the bearer token, which an <img src> cannot send — fetch it as a
 *  blob and hand back an object URL (the caller revokes it). */
export async function fetchScreenshotUrl(id: string): Promise<string | null> {
  const token = localStorage.getItem('suryaiq_token');
  const res = await fetch(`/api/feedback/${encodeURIComponent(id)}/screenshot`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: 'include',
  });
  if (!res.ok) return null;
  return URL.createObjectURL(await res.blob());
}
